<script lang="ts">
  import axios from 'axios'
  import Cookies from 'js-cookie'
  import moment from 'moment'
  import { isRelativeDate, textColor } from '@/utils'
  import { onDestroy, onMount, tick } from 'svelte'
  import Coupon from './Coupon.svelte'

  let {
    coupon = {}
  } = $props()

  coupon = JSON.parse(coupon)

  let mode = $state('coupon')
  let showConfirmRedemption = $state(false)
  let redemptions = $state([])
  let redemptionsLoaded = $state(false)
  let redemptionInProgress = $state(false)
  let fingerprint = $state(null)
  let loaded = $state(false)
  let completeRedemptionClicked = $state(false)
  let scrollY = $state(0)
  let scrollable = $state(false)

  onMount(() => {
    window.addEventListener('scroll', windowScrolled)
    window.addEventListener('resize', testScrollable)

    // so Fingerprintjs2 seems to be unreliable?, at least as far
    // as the fact that we get people saying "it says I redeemed it already"
    // ...so we'll just make a totally random ID instead
    window.setTimeout(() => {
      fingerprint = getOrSetDeviceId()
      loadRedemptions()
    }, 25)
  })

  onDestroy(() => {
    window.removeEventListener('scroll', windowScrolled)
    window.removeEventListener('resize', testScrollable)
  })

  // If multiple redemptions allowed, it's never 'redeemed'.
  // Else show as redeemed if it was redeemed > 15 minutes ago,
  // or if the 'Complete Redemption' button was clicked
  let isRedeemed = $derived.by(() => {
    // NOTE: Dates come back from the db in UTC, albeit without a TZ indicator.
    //       We have to append 'Z' to get JS to parse as UTC

    let foo = redemptions[0] ? moment(new Date(redemptions[0].created_at + 'Z')) : 'no redemptions'
    Rollbar.info('PublicCoupon.isRedeemed', {
      what: 'PublicCoupon.isRedeemed',
      'completeRedemptionClicked': completeRedemptionClicked,
      'coupon.single_redemption_only': coupon.single_redemption_only,
      'lastRedemption': lastRedemption,
      "moment(new Date(redemptions[0].created_at + 'Z'))": foo,
      "moment().utc().add(-15, 'minutes')": moment().utc().add(-15, 'minutes'),
      "fingerprint": fingerprint,
      "Cookies.get('did')": Cookies.get('did'),
      'localStorage.did': localStorage.did,
    })

    return completeRedemptionClicked ||
      (
        coupon.single_redemption_only &&
        lastRedemption &&
        moment(new Date(redemptions[0].created_at + 'Z')) < moment().utc().add(-15, 'minutes')
      )
  })

  let lastRedemption = $derived(redemptions.length ? redemptions[0] : null)

  async function testScrollable() {
    window.scrollTo(0, 0)
    window.scrollBy(0, 1)
    await tick()

    if (window.scrollY === 1) {
      scrollable = true
      window.scrollTo(0, 0)
    } else {
      scrollable = false
    }
  }

  let scrollHintClass = $derived.by(() => {
    let scrolled = scrollY > 0
    return `coupon__scroll-hint ${!loaded || scrolled || !scrollable ? 'scrolled' : ''}`
  })

  let publicCoupon = $derived.by(() => {
    let publicCoupon = { ...coupon }
    if (publicCoupon.has_expiration_date) {
      publicCoupon.expires_at = calculateExpiration()
    }

    return publicCoupon
  })

  let shouldCreateRedemption = $derived(!coupon.single_redemption_only || !redemptions.length)

  // find or create the DID, update its cookie, return it
  function getOrSetDeviceId() {
    let did = Cookies.get("did")

    if (!did) {
      did = randomChars() + randomChars() + randomChars() + randomChars()
    }

    Cookies.set("did", did, { expires: 365 * 5 })
    localStorage.did = did
    return did
  }

  function randomChars() {
    return Math.random().toString(36).slice(2)
  }

  // The initial "Complete redemption" click
  function beginRedemption() {
    if (!redemptionsLoaded) {
      // ideally we'd try to load again
      return false
    }

    if (coupon.single_redemption_only) {
      if (lastRedemption) {
        showConfirmRedemption = false
        mode = 'redeem'
      } else {
        showConfirmRedemption = true
      }
    } else {
      setRedeemed()
    }
  }

  // After "yes, redeem", if the customer clicks "Complete redemption"
  function completeRedemption() {
    completeRedemptionClicked = true
    showConfirmRedemption = false
    mode = 'coupon'
  }

  function confirmRedemption() {
    setRedeemed()
  }

  function cancelRedemption() {
    showConfirmRedemption = false
  }

  function calculateExpiration() {
    let c = coupon
    let uuid = location.pathname.split("coupons/")[1]
    let cookieName = `${uuid}-exp`
    let exp_cookie, exp_coupon, exp_differs

    // Need to recalculate for fixed expiration coupons in case the
    // owner changed the expiration date
    if (!isRelativeDate(c.expiration_date) && (exp_cookie = Cookies.get(cookieName))) {
      exp_coupon = moment(`${c.expiration_date} ${c.expiration_time}`, "MM/DD/YYYY HH:mmap").toString()
      exp_differs = exp_coupon != exp_cookie
    }

    if (exp_differs || !Cookies.get(cookieName)) {
      let exp = moment(Date.parse(c.expires_at))
      Cookies.set(cookieName, exp, { expires: 365 * 5 })
    }
    return Cookies.get(cookieName)
  }

  function loadRedemptions() {
    axios.get(`/coupon_redemptions/for/${fingerprint}`, {
      params: {
        coupon: coupon.uuid
      }
    })
      .then((response) => {
        redemptions = response.data
        redemptionsLoaded = true
        redemptionInProgress = false
        loaded = true
        window.setTimeout(testScrollable, 10)
      })
      .catch(
        error => {
          Rollbar.error('PublicCoupon.loadRedemptions', {
            error: error,
            "fingerprint": fingerprint,
            "Cookies.get('did')": Cookies.get('did'),
            'localStorage.did': localStorage.did,
          })
        }
      )
  }

  // When the customer clicks "yes, redeem"
  function setRedeemed() {
    if (shouldCreateRedemption) {
      redemptionInProgress = true

      // send a request to server to mark as redeemed
      axios.post('/coupon_redemptions.json', {
        coupon: coupon.uuid,
        did: fingerprint
      })
        .then(() => {
          loadRedemptions()
        })
    }

    mode = 'redeem'
  }

  function windowScrolled() {
    scrollY = window.scrollY
  }
</script>

{#if loaded}
  <div class="PublicCoupon">
    <div class="{scrollHintClass}" style="background-color: {coupon.color}; color: {textColor(coupon.color)}">
      <i>⇩</i>
    </div>
    <Coupon
      coupon={publicCoupon}
      {mode}
      {beginRedemption}
      {completeRedemption}
      {confirmRedemption}
      {cancelRedemption}
      {showConfirmRedemption}
      {isRedeemed}
      {redemptionInProgress}
      {lastRedemption}
      {loaded}
      live={true}
    />
  </div>
{/if}
